import Link from 'redux-first-router-link'
import { selectLocationState } from 'redux-first-router'
import { createSelector } from 'reselect'
import { emptyObj } from 'util/objects'
import { emptyArr } from 'util/arrays'
import { CHAT_PAGE } from 'subapps/chat/pages'
import { queryIdToQuery } from '../utils/query'

function selectBase(state) {
  return state.searchV2 || emptyObj
}

export const selectByQueryId = createSelector(
  selectBase,
  base => base.byQueryId
)

export const selectCurrentQueryId = createSelector(
  selectBase,
  base => base.currentQueryId
)

export function selectLastUpdatedAt(state) {
  return selectBase(state).lastUpdatedAt
}

export function selectQueryIdFromProps(_, props) {
  return props.queryId || null
}

export const selectCurrentSearchQuery = createSelector(
  selectByQueryId,
  selectCurrentQueryId,
  (byQueryId, queryId) => {
    return byQueryId[queryId] || emptyObj
  }
)

export const selectCurrentSearchQueryTotalCount = createSelector(
  selectCurrentSearchQuery,
  currentQuery => {
    return currentQuery.conversationCount || 0
  }
)

export const selectCurrentSearchQueryFilter = createSelector(
  selectCurrentSearchQuery,
  selectCurrentQueryId,
  currentQuery => currentQuery.filter || emptyObj
)

export const selectCurrentSearchQueryIsLoading = createSelector(
  selectCurrentSearchQuery,
  currentQuery => !!currentQuery.loading
)

export const selectCurrentSearchQueryIsLoaded = createSelector(
  selectCurrentSearchQuery,
  currentQuery => !!currentQuery.loaded
)

export const selectCurrentSearchPageCursor = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.currentPageCursor || null
)

export const selectNextSearchPageCursor = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.nextPageCursor || null
)

export const selectCurrentSearchHasMore = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.hasAllPages !== true
)

export const selectCurrentSearchConversationIds = createSelector(
  selectCurrentSearchQuery,
  currentQuery => currentQuery.conversationIds || emptyArr
)

export const selectCurrentSearchQueryIsLoadingMore = createSelector(
  selectCurrentSearchQueryIsLoading,
  selectCurrentSearchQueryIsLoaded,
  (isLoading, isLoaded) => isLoading && isLoaded
)

export const selectCurrentSearchIsError = createSelector(
  selectByQueryId,
  selectCurrentQueryId,
  (byQueryId, queryId) => !!(byQueryId[queryId] || emptyObj).error
)

export const selectCurrentConversationId = createSelector(
  selectLocationState,
  location =>
    location &&
    location.query &&
    location.query.conversationId &&
    // Not a big fan of parsing the roomId to integer here, but as far as I can see the location
    // reducer is part of the redux-first-router and I dont have a way to tell it to save this variable
    // as a integer
    parseInt(location.query.conversationId, 10)
)

export function makeSelectConversationCountByQueryId() {
  return createSelector(
    selectByQueryId,
    selectQueryIdFromProps,
    (searchesByQueryId, queryId) => {
      const { conversationCount = 0 } = searchesByQueryId[queryId] || emptyObj
      return conversationCount
    }
  )
}

export const selectSortKeyForAgent = () => 'LAST_USER_OR_AGENT_MESSAGE_AT'

const hackAgentSortOptions = [
  {
    id: 'newest',
    name: 'Newest',
    orderBy: 'LAST_USER_OR_AGENT_MESSAGE_AT_DESC',
  },
  {
    id: 'oldest',
    name: 'Oldest',
    orderBy: 'LAST_USER_OR_AGENT_MESSAGE_AT_ASC',
  },
  {
    id: 'longestWaiting',
    name: 'Waiting longest',
    orderBy: 'LAST_UNANSWERED_USER_MESSAGE_AT_ASC',
  },
]

export const selectAgentSortOptions = () => hackAgentSortOptions

export const selectSortOptions = createSelector(
  selectCurrentQueryId,
  selectAgentSortOptions,
  (currentQueryId, agentSortOptions) => {
    const { orderBy: currentOrderBy } = queryIdToQuery(currentQueryId) || {}
    return agentSortOptions.map(({ id, name, orderBy }) => ({
      active: currentOrderBy === orderBy,
      as: Link,
      key: id,
      text: name,
      to: {
        type: CHAT_PAGE,
        payload: {
          query: queryIdToQuery(`${currentQueryId} orderBy:${orderBy}`),
        },
      },
      value: id,
    }))
  }
)

export const selectCurrentSearchNextConversationId = createSelector(
  selectCurrentSearchConversationIds,
  selectCurrentConversationId,
  (ids, currentConversationId) => {
    if (!ids) return null
    if (!currentConversationId) return null
    const currentTicketIndex = ids.indexOf(currentConversationId)
    return ids[currentTicketIndex + 1]
  }
)

export const selectCurrentSearchPreviousConversationId = createSelector(
  selectCurrentSearchConversationIds,
  selectCurrentConversationId,
  (ids, currentConversationId) => {
    if (!ids) return null
    if (!currentConversationId) return null
    const currentTicketIndex = ids.indexOf(currentConversationId)
    return ids[currentTicketIndex - 1]
  }
)
